/* ==========================================================================
   Zooted Prestige — locator.js
   "Find Zooted" retailer locator: search, filters, geolocation, distance
   sorting, list/map views. Works with Google Maps, Mapbox, or no map at all.

   DATA SOURCES (merged, later overrides earlier by id):
     1. assets/locator-data.js  -> window.ZOOTED_RETAILERS
     2. Theme Editor blocks     -> <script type="application/json" data-locator-blocks>
   The map API key is entered in Theme Settings > Retailer locator and map.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-locator]');
  if (!root) return;

  var settings = (window.zootedSettings && window.zootedSettings.map) || {};
  var PRODUCT_LABELS = {
    'cyber-stix': 'Cyber Stix',
    'infused-pre-rolls': 'Infused Pre-Rolls',
    'classic-pre-rolls': 'Classic Pre-Rolls',
    'flower': 'Flower',
    'baller-buckets': 'Baller Buckets',
    'gummies': 'Gummies',
    'chocolate': 'Chocolate',
    'concentrates': 'Concentrates'
  };

  /* ------------------------------------------------------------------
     Data
     ------------------------------------------------------------------ */
  var retailers = (window.ZOOTED_RETAILERS || []).slice();
  var blockScript = root.querySelector('[data-locator-blocks]');
  if (blockScript) {
    try {
      var blocks = JSON.parse(blockScript.textContent) || [];
      blocks.forEach(function (b) {
        if (!b || !b.name) return;
        var idx = retailers.findIndex(function (r) { return r.id === b.id; });
        if (idx !== -1) retailers[idx] = Object.assign({}, retailers[idx], b);
        else retailers.push(b);
      });
    } catch (err) { /* malformed block JSON — ignore */ }
  }
  retailers = retailers.filter(function (r) { return r && r.name; });

  var state = {
    query: '',
    products: [],
    radius: 0,           // 0 = any distance
    origin: null,        // {lat, lng}
    activeId: null
  };

  var listEl = root.querySelector('[data-locator-list]');
  var mapEl = root.querySelector('[data-locator-map]');
  var emptyEl = root.querySelector('[data-locator-empty]');
  var liveEl = root.querySelector('[data-locator-live]');
  var map = null, markers = [], mapReady = false;

  /* ------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------ */
  function haversine(a, b) {
    var R = 3958.8; // miles
    var dLat = (b.lat - a.lat) * Math.PI / 180;
    var dLng = (b.lng - a.lng) * Math.PI / 180;
    var s = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  }

  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function announce(msg) { if (liveEl) { liveEl.textContent = ''; setTimeout(function () { liveEl.textContent = msg; }, 60); } }

  function filtered() {
    var q = state.query.trim().toLowerCase();
    var list = retailers.filter(function (r) {
      if (q) {
        var haystack = [r.name, r.city, r.zip, r.address].join(' ').toLowerCase();
        if (haystack.indexOf(q) === -1) return false;
      }
      if (state.products.length) {
        var carries = r.products || [];
        var ok = state.products.every(function (p) { return carries.indexOf(p) !== -1; });
        if (!ok) return false;
      }
      return true;
    });

    if (state.origin) {
      list.forEach(function (r) {
        r._distance = (r.lat && r.lng) ? haversine(state.origin, { lat: r.lat, lng: r.lng }) : null;
      });
      if (state.radius > 0) {
        list = list.filter(function (r) { return r._distance !== null && r._distance <= state.radius; });
      }
      list.sort(function (a, b) {
        var fa = a.featured ? 1 : 0, fb = b.featured ? 1 : 0;
        if (fa !== fb) return fb - fa;
        return (a._distance == null ? 1e9 : a._distance) - (b._distance == null ? 1e9 : b._distance);
      });
    } else {
      list.forEach(function (r) { r._distance = null; });
      list.sort(function (a, b) {
        var fa = a.featured ? 1 : 0, fb = b.featured ? 1 : 0;
        if (fa !== fb) return fb - fa;
        return String(a.city).localeCompare(String(b.city)) || String(a.name).localeCompare(String(b.name));
      });
    }
    return list;
  }

  /* ------------------------------------------------------------------
     List rendering
     ------------------------------------------------------------------ */
  function renderList() {
    var list = filtered();
    if (emptyEl) emptyEl.hidden = list.length !== 0;

    listEl.innerHTML = list.map(function (r) {
      var mapsQuery = encodeURIComponent(r.name + ' ' + r.address + ' ' + r.city + ' ' + (r.state || 'MI') + ' ' + (r.zip || ''));
      var chips = (r.products || []).map(function (p) {
        return '<span>' + esc(PRODUCT_LABELS[p] || p) + '</span>';
      }).join('');
      return (
        '<article class="store-card' + (state.activeId === r.id ? ' is-active' : '') + '" data-store-id="' + esc(r.id) + '" tabindex="0">' +
          '<h3 class="store-card__name">' + esc(r.name) +
            (r.featured ? ' <span class="badge badge--yellow">Featured Retailer</span>' : '') + '</h3>' +
          '<p class="store-card__addr">' + esc(r.address) + ', ' + esc(r.city) + ', ' + esc(r.state || 'MI') + ' ' + esc(r.zip || '') + '</p>' +
          (r._distance != null ? '<p class="store-card__distance">' + r._distance.toFixed(1) + ' mi away</p>' : '') +
          (chips ? '<div class="store-card__products">' + chips + '</div>' : '') +
          '<div class="store-card__links">' +
            '<a href="https://www.google.com/maps/dir/?api=1&destination=' + mapsQuery + '" target="_blank" rel="noopener">Directions<span class="visually-hidden"> to ' + esc(r.name) + ' (opens in a new window)</span></a>' +
            (r.phone ? '<a href="tel:' + esc(r.phone.replace(/[^0-9+]/g, '')) + '">' + esc(r.phone) + '</a>' : '') +
            (r.website ? '<a href="' + esc(r.website) + '" target="_blank" rel="noopener">Website<span class="visually-hidden"> (opens in a new window)</span></a>' : '') +
            (r.hours ? '<span class="text-mid">' + esc(r.hours) + '</span>' : '') +
          '</div>' +
        '</article>'
      );
    }).join('');

    announce(list.length === 0
      ? 'No retailers match your search. Try widening your filters.'
      : 'Retailer list updated.');
    renderMarkers(list);
  }

  /* ------------------------------------------------------------------
     Map providers
     ------------------------------------------------------------------ */
  var GRAYSCALE_STYLE = [
    { elementType: 'geometry', stylers: [{ saturation: -100 }] },
    { elementType: 'labels.text.fill', stylers: [{ saturation: -100 }, { lightness: -20 }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ lightness: 30 }, { saturation: -100 }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] }
  ];

  function defaultCenter() {
    var parts = String(settings.center || '44.3148,-85.6024').split(',');
    return { lat: parseFloat(parts[0]) || 44.3148, lng: parseFloat(parts[1]) || -85.6024 };
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src; s.async = true; s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function loadStylesheet(href) {
    return new Promise(function (resolve) {
      var l = document.createElement('link');
      l.rel = 'stylesheet'; l.href = href;
      l.onload = resolve; document.head.appendChild(l);
      setTimeout(resolve, 2500); // never block on a slow stylesheet
    });
  }

  function mapBooted() {
    mapReady = true;
    var fb = mapEl.querySelector('.locator-map__fallback');
    if (fb) fb.remove();
    renderMarkers(filtered());
  }

  function initMap() {
    if (!mapEl) return;
    var provider = settings.provider || 'leaflet';
    if (provider === 'none') return; // styled list only — fallback panel already rendered
    var key = settings.apiKey;
    if ((provider === 'google' || provider === 'mapbox') && !key) return; // key missing — fallback explains

    if (provider === 'leaflet') {
      // OpenStreetMap via Leaflet — interactive map with no API key required.
      Promise.all([
        loadStylesheet('https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css'),
        loadScript('https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js')
      ]).then(function () {
        var c = defaultCenter();
        map = L.map(mapEl, { scrollWheelZoom: true, zoomControl: true });
        map.setView([c.lat, c.lng], Number(settings.zoom) || 6);
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors'
        }).addTo(map);
        mapBooted();
      }).catch(function () { announce('The map could not be loaded. The retailer list is still available.'); });
      return;
    }

    if (provider === 'google') {
      window.zootedGmapsInit = function () {
        map = new google.maps.Map(mapEl, {
          center: defaultCenter(),
          zoom: Number(settings.zoom) || 6,
          styles: GRAYSCALE_STYLE,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false
        });
        mapReady = true;
        var fb = mapEl.querySelector('.locator-map__fallback');
        if (fb) fb.remove();
        renderMarkers(filtered());
      };
      loadScript('https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) + '&callback=zootedGmapsInit')
        .catch(function () { announce('The map could not be loaded. The retailer list is still available.'); });
    } else if (provider === 'mapbox') {
      Promise.all([
        loadScript('https://api.mapbox.com/mapbox-gl-js/v3.5.1/mapbox-gl.js'),
        new Promise(function (res) {
          var l = document.createElement('link');
          l.rel = 'stylesheet'; l.href = 'https://api.mapbox.com/mapbox-gl-js/v3.5.1/mapbox-gl.css';
          l.onload = res; document.head.appendChild(l); setTimeout(res, 2000);
        })
      ]).then(function () {
        mapboxgl.accessToken = key;
        var c = defaultCenter();
        map = new mapboxgl.Map({
          container: mapEl,
          style: 'mapbox://styles/mapbox/light-v11',
          center: [c.lng, c.lat],
          zoom: Number(settings.zoom) || 6
        });
        map.addControl(new mapboxgl.NavigationControl(), 'top-right');
        mapReady = true;
        var fb = mapEl.querySelector('.locator-map__fallback');
        if (fb) fb.remove();
        renderMarkers(filtered());
      }).catch(function () { announce('The map could not be loaded. The retailer list is still available.'); });
    }
  }

  function clearMarkers() {
    markers.forEach(function (m) {
      if (m.setMap) m.setMap(null);      // google
      else if (m.remove) m.remove();     // mapbox + leaflet
    });
    markers = [];
  }

  function renderMarkers(list) {
    if (!mapReady || !map) return;
    clearMarkers();
    var provider = settings.provider;
    var bounds = null;

    list.forEach(function (r) {
      if (!r.lat || !r.lng) return;
      if (provider === 'leaflet') {
        var icon = L.divIcon({
          className: 'map-marker' + (r.featured ? ' is-featured' : ''),
          iconSize: [26, 26],
          iconAnchor: [13, 26],
          popupAnchor: [0, -26]
        });
        var lm = L.marker([r.lat, r.lng], { icon: icon, title: r.name, alt: r.name, keyboard: true })
          .addTo(map)
          .bindPopup('<strong>' + esc(r.name) + '</strong><br>' + esc(r.address) + ', ' + esc(r.city));
        lm.on('click', function () { focusStore(r.id, true); });
        markers.push(lm);
        bounds = bounds || L.latLngBounds([]);
        bounds.extend([r.lat, r.lng]);
      } else if (provider === 'google') {
        var marker = new google.maps.Marker({
          position: { lat: r.lat, lng: r.lng },
          map: map,
          title: r.name,
          icon: {
            path: 'M 0,10 L -8,0 L -5,-8 L 0,-10 L 5,-8 L 8,0 Z',
            fillColor: r.featured ? '#FFD400' : '#00E7FF',
            fillOpacity: 1,
            strokeColor: '#050505',
            strokeWeight: 1.4,
            scale: 1.3
          }
        });
        marker.addListener('click', function () { focusStore(r.id, true); });
        markers.push(marker);
        bounds = bounds || new google.maps.LatLngBounds();
        bounds.extend(marker.getPosition());
      } else if (provider === 'mapbox') {
        var el = document.createElement('button');
        el.className = 'map-marker' + (r.featured ? ' is-featured' : '');
        el.setAttribute('aria-label', r.name);
        el.addEventListener('click', function () { focusStore(r.id, true); });
        var mk = new mapboxgl.Marker({ element: el }).setLngLat([r.lng, r.lat]).addTo(map);
        markers.push(mk);
        bounds = bounds || new mapboxgl.LngLatBounds();
        bounds.extend([r.lng, r.lat]);
      }
    });

    if (bounds && list.length > 1) {
      if (provider === 'google') map.fitBounds(bounds, 48);
      else if (provider === 'leaflet') map.fitBounds(bounds, { padding: [48, 48], maxZoom: 12 });
      else map.fitBounds(bounds, { padding: 48, maxZoom: 12 });
    } else if (bounds && list.length === 1) {
      var only = list[0];
      if (provider === 'google') { map.setCenter({ lat: only.lat, lng: only.lng }); map.setZoom(12); }
      else if (provider === 'leaflet') { map.setView([only.lat, only.lng], 12); }
      else { map.flyTo({ center: [only.lng, only.lat], zoom: 12 }); }
    }
  }

  function focusStore(id, scrollList) {
    state.activeId = id;
    listEl.querySelectorAll('.store-card').forEach(function (card) {
      var active = card.getAttribute('data-store-id') === id;
      card.classList.toggle('is-active', active);
      if (active && scrollList) card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }

  /* ------------------------------------------------------------------
     Controls
     ------------------------------------------------------------------ */
  var form = root.querySelector('[data-locator-form]');
  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var input = form.querySelector('input[type="search"], input[type="text"]');
      state.query = input ? input.value : '';
      // If the query looks like a ZIP and we have coordinates for a matching store,
      // use the first match as a soft origin for distance sorting.
      var zipMatch = state.query.trim().match(/^\d{5}$/);
      if (zipMatch) {
        var hit = retailers.find(function (r) { return r.zip === zipMatch[0] && r.lat; });
        if (hit) state.origin = { lat: hit.lat, lng: hit.lng };
      }
      renderList();
    });
  }

  root.addEventListener('click', function (e) {
    var filter = e.target.closest('[data-locator-filter]');
    if (filter) {
      var val = filter.getAttribute('data-locator-filter');
      var idx = state.products.indexOf(val);
      idx === -1 ? state.products.push(val) : state.products.splice(idx, 1);
      filter.setAttribute('aria-pressed', idx === -1 ? 'true' : 'false');
      renderList();
    }

    if (e.target.closest('[data-locator-geolocate]')) {
      var btn = e.target.closest('[data-locator-geolocate]');
      if (!navigator.geolocation) { announce('Location detection is not available in this browser.'); return; }
      btn.classList.add('is-disabled');
      navigator.geolocation.getCurrentPosition(function (pos) {
        state.origin = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        btn.classList.remove('is-disabled');
        announce('Sorted by distance from your location.');
        renderList();
      }, function () {
        btn.classList.remove('is-disabled');
        announce('We could not detect your location. Try searching by city or ZIP instead.');
      }, { timeout: 8000 });
    }

    var toggle = e.target.closest('[data-locator-view]');
    if (toggle) {
      var layout = root.querySelector('.locator-layout');
      var showMap = toggle.getAttribute('data-locator-view') === 'map';
      layout.classList.toggle('show-map', showMap);
      root.querySelectorAll('[data-locator-view]').forEach(function (b) {
        b.setAttribute('aria-pressed', b === toggle ? 'true' : 'false');
      });
      if (showMap && map) {
        if (settings.provider === 'mapbox' && map.resize) map.resize();
        if (settings.provider === 'leaflet' && map.invalidateSize) map.invalidateSize();
      }
    }

    var card = e.target.closest('.store-card');
    if (card && !e.target.closest('a')) {
      focusStore(card.getAttribute('data-store-id'), false);
    }
  });

  var radiusSelect = root.querySelector('[data-locator-radius]');
  if (radiusSelect) {
    radiusSelect.addEventListener('change', function () {
      state.radius = Number(radiusSelect.value) || 0;
      if (state.radius > 0 && !state.origin) {
        announce('Choose "Use my location" or search a ZIP code to apply a radius.');
      }
      renderList();
    });
  }

  /* ------------------------------------------------------------------
     Boot — honor ?q= and ?geo=1 passed from the homepage preview
     ------------------------------------------------------------------ */
  (function bootParams() {
    var params = new URLSearchParams(window.location.search);
    var q = params.get('q');
    if (q) {
      state.query = q;
      var input = root.querySelector('[data-locator-form] input');
      if (input) input.value = q;
      var zip = q.trim().match(/^\d{5}$/);
      if (zip) {
        var hit = retailers.find(function (r) { return r.zip === zip[0] && r.lat; });
        if (hit) state.origin = { lat: hit.lat, lng: hit.lng };
      }
    }
    if (params.get('geo') === '1') {
      var geoBtn = root.querySelector('[data-locator-geolocate]');
      if (geoBtn) setTimeout(function () { geoBtn.click(); }, 400);
    }
  })();

  renderList();
  initMap();
})();
